/**
 * Single source for vetted legal / liability strings used in product UI (chatbot, AMA, professional notice).
 * Import from `@healthdesk/shared-utils` in apps and packages — do not duplicate long copy in components.
 */

/** Inline Alert under chat (patient vs professional wording). */
export const LEGAL_COPY_CHATBOT_ALERT = {
  professional:
    "My HealthDesk Medical SuperIntelligence is expert-curated artificial intelligence for clinical decision support and education only. It does not provide patient care and can be wrong. You remain responsible for all clinical decisions; verify against the chart, exam, and guidelines.",
  patient:
    "My HealthDesk Medical SuperIntelligence is informational software. It is not a clinician and cannot provide medical care, diagnosis, or treatment. For emergencies, call your local emergency number (911 in the U.S.). Confirm everything with a licensed health care professional.",
};

/** Ask Me Anything / Query Chat notice below the input (professional-oriented). */
export const LEGAL_COPY_AI_ASSISTANT_INPUT_NOTICE =
  "This assistant uses artificial intelligence, which can be wrong or incomplete. It is intended to support your professional judgment—not replace it. Verify information with primary sources and patient-specific data before clinical use. For possible emergencies, use local emergency services; do not rely on this chat for triage.";

/** Modal shown to users with role professional on first load (HealthDesk Professional). */
export const LEGAL_COPY_PROFESSIONAL_DIALOG = {
  title: "Important Notice for Medical Professionals",
  paragraphs: [
    "The materials on this site are provided for general medical education purposes only and should not be applied rigidly or universally in any clinical scenario. Decisions regarding patient care must remain the professional responsibility of the individual medical practitioner, who must use clinical judgment based on the unique circumstances of each case.",
    "HealthDesk Professional is intended strictly for use by medical professionals. Neither HealthDesk nor its representatives assume legal, financial, or medical liability for decisions made using, or based on, the information provided through its platform.",
    "Output may be inaccurate; you must exercise independent clinical judgment.",
    "Non-clinical users should not use this product for medical decisions; seek a licensed clinician. Any use by non-medical professionals is entirely at your own risk.",
    "By accessing and using this site, you agree to these conditions as well as the full HealthDesk Terms of Use.",
  ],
};

/**
 * @param {string | undefined} role - `userData.role` (effective role)
 * @returns {string}
 */
export function getChatbotAlertDisclaimerText(role) {
  return role === "professional"
    ? LEGAL_COPY_CHATBOT_ALERT.professional
    : LEGAL_COPY_CHATBOT_ALERT.patient;
}

/**
 * Shorter inline Alert copy used on some unauthenticated / alternate chat shells
 * (`apps/unified-app` local Disclaimer). Keep in sync with compliance review when changing.
 */
export const LEGAL_COPY_CHATBOT_ALERT_COMPACT = {
  professional:
    "My HealthDesk Medical SuperIntelligence is expert-curated artificial intelligence for educational purposes only. It can make mistakes and cannot provide health care. Exercise professional judgement and independently verify any information presented here.",
  patient:
    "My HealthDesk Medical SuperIntelligence is expert-curated artificial intelligence. It can make mistakes and cannot provide health care. Verify all medical information with a health care professional.",
};

/**
 * @param {string | undefined} role
 * @returns {string}
 */
export function getChatbotAlertDisclaimerTextCompact(role) {
  return role === "professional"
    ? LEGAL_COPY_CHATBOT_ALERT_COMPACT.professional
    : LEGAL_COPY_CHATBOT_ALERT_COMPACT.patient;
}
